import { useEffect, useState, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import WorkspaceShell from '../../ui/layout/WorkspaceShell';
import ChatPane from '../../ui/chat/ChatPane';
import { useClients } from '../../context/ClientContext';
import { useUserProfile } from '../../context/UserProfileContext';
import LoadingDots from '../../components/LoadingDots/LoadingDots';
// import FileUploadPanel from '../../ui/upload/FileUploadPanel';
import ConversationHistoryPanel from '../../ui/chat/ConversationHistoryPanel';
import WorkspaceNav from '../../ui/layout/WorkspaceNav';
import PolicyDashboard from '../../ui/PolicyExtraction/PolicyDashboard/PolicyDashboard';
import PolicyDetails from '../../ui/PolicyExtraction/PolicyDetails/PolicyDetails';
import UploadDashboard from '../../ui/PolicyAnalysis/UploadDashboard/UploadDashboard';
import AnalysisDashboard from '../../ui/PolicyAnalysis/AnalysisDashboard/AnalysisDashboard';
import AnalysisDetails from '../../ui/PolicyAnalysis/AnalysisDetails/AnalysisDetails';
import ConfigurationUploadDashboard from '../../ui/ConfigurationComparison/ConfigurationUploadDashboard/ConfigurationUploadDashboard';
import ConfigurationComparisonDashboard from '../../ui/ConfigurationComparison/ConfigurationComparisonDashboard/ConfigurationComparisonDashboard';
import ComparisonResult from '../../ui/ConfigurationComparison/ComparisonResult/ComparisonResult';
import type { PolicyExtractionDTO, PolicyAnalysisDTO, ConfigurationComparisonDTO } from '../../infrastructure/models';
import SettingNav from '../../ui/layout/SettingNav';
import UserSettings from '../Settings/UserSettings';
import TenantSettings from '../Settings/TenantSettings';
import EnvironmentSettings from '../Settings/EnvironmentSettings';
import PrivacyNoticeAdmin from '../../components/PrivacyNoticeAdmin/PrivacyNoticeAdmin';
import DisclaimerAdmin from '../../components/DisclaimerAdmin/DisclaimerAdmin';


const WorkspacePage = () => {
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [view, setView] = useState<'chat' | /* 'upload' | */ 'policy' | 'analysis' | 'configComparison' | 'settings'>('chat');
  const [settingsView, setSettingsView] = useState<'user' | 'tenant' | 'environment' | 'privacy' | 'disclaimer'>('user');
  const [configurationView, setConfigurationView] = useState<'list' | 'analysisDashboard' | 'analysisDetails'>('list');
  const [configComparisonView, setConfigComparisonView] = useState<'uploads' | 'comparisonDashboard' | 'comparisonResult'>('uploads');
  const [selectedConfigComparison, setSelectedConfigComparison] = useState<ConfigurationComparisonDTO | null>(null);
  const [hasActiveMessages, setHasActiveMessages] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyExtractionDTO | null>(null);
  const [showPolicyDetails, setShowPolicyDetails] = useState(false);
  const [selectedComparison, setSelectedComparison] = useState<PolicyAnalysisDTO | null>(null);
  const { activeClient, clients, loading: clientsLoading } = useClients();
  const { profile, loading: profileLoading } = useUserProfile();

  const isProfileReady = !profileLoading && Boolean(profile);
  const isClientsReady = !clientsLoading && clients.length > 0 && Boolean(activeClient);

  // Placeholder load
  useEffect(() => {
    // could load initial conversation list
  }, []);

  const handleSelect = useCallback((id: string) => {
    if (!id) {
      setActiveConversationId(null);
      setHasActiveMessages(false);
    } else {
      setActiveConversationId(id);
    }
  }, []);
  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    setHasActiveMessages(false);
  }, []);
  const handleNavChange = useCallback((key: 'chat' | /* 'upload' | */ 'policy' | 'analysis' | 'configComparison' | 'settings') => {
    setView(key);
    if (key !== 'policy') {
      setSelectedPolicy(null);
      setShowPolicyDetails(false);
    }
    if (key !== 'analysis') {
      setConfigurationView('list');
      setSelectedComparison(null);
    }
    if (key !== 'configComparison') {
      setConfigComparisonView('uploads');
      setSelectedComparisonId(null);
      setSelectedConfigComparison(null);
    }
  }, []);

  const handleOpenComparisonDashboard = useCallback(() => {
    setConfigurationView('analysisDashboard');
    setSelectedComparison(null);
  }, []);

  const handleBackToConfigurationList = useCallback(() => {
    setConfigurationView('list');
    setSelectedComparison(null);
  }, []);

  const handleOpenAnalysisDetails = useCallback((analysis: PolicyAnalysisDTO) => {
    setSelectedComparison(analysis);
    setConfigurationView('analysisDetails');
  }, []);

  const handleBackToAnalysisDashboard = useCallback(() => {
    setConfigurationView('analysisDashboard');
  }, []);

  const handleNavigateToComparisonDashboard = useCallback(() => {
    setConfigComparisonView('comparisonDashboard');
  }, []);

  const handleBackToConfigUploads = useCallback(() => {
    setConfigComparisonView('uploads');
  }, []);

  const [selectedComparisonId, setSelectedComparisonId] = useState<string | null>(null);

  const handleOpenComparisonResult = useCallback((comparison: ConfigurationComparisonDTO) => {
    setSelectedComparisonId(comparison.id);
    setSelectedConfigComparison(comparison);
    setConfigComparisonView('comparisonResult');
  }, []);

  const handleBackToComparisonDashboard = useCallback(() => {
    setSelectedComparisonId(null);
    setSelectedConfigComparison(null);
    setConfigComparisonView('comparisonDashboard');
  }, []);

  useEffect(() => {
    if (view !== 'policy') {
      setSelectedPolicy(null);
      setShowPolicyDetails(false);
    }
  }, [view]);

  useEffect(() => {
    setActiveConversationId(null);
    setHasActiveMessages(false);
  }, [activeClient?.id]);
  const mainContent = useMemo(() => {
    return (
      <AnimatePresence mode="wait">
        {view === 'chat' && (
          <motion.div
            key="chat"
            className="h-full flex flex-col"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}>
            <ChatPane
              conversationId={activeConversationId}
              onCreated={(id) => setActiveConversationId(id)}
              onHasMessagesChange={setHasActiveMessages}
            />
          </motion.div>
        )}
        {/* {view === 'upload' && (
          <motion.div
            key="upload"
            className="h-full overflow-y-auto p-4"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}>
            <FileUploadPanel />
          </motion.div>
        )} */}
        {view === 'policy' && (
          <motion.div
            key="policy"
            className="h-full overflow-y-auto"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}>
            {showPolicyDetails ? (
              <PolicyDetails
                policy={selectedPolicy}
                onBack={() => {
                  setShowPolicyDetails(false);
                  setSelectedPolicy(null);
                }}
              />
            ) : (
              <PolicyDashboard
                onPolicySelect={(policy) => {
                  setSelectedPolicy(policy);
                  setShowPolicyDetails(true);
                }}
              />
            )}
          </motion.div>
        )}
        {view === 'analysis' && (
          <motion.div
            key="analysis"
            className="h-full overflow-y-auto"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}>
            {configurationView === 'analysisDashboard' && (
              <AnalysisDashboard
                onBack={handleBackToConfigurationList}
                onViewDetails={handleOpenAnalysisDetails}
              />
            )}
            {configurationView === 'analysisDetails' && (
              <AnalysisDetails
                analysis={selectedComparison}
                onBack={handleBackToAnalysisDashboard}
                tenantId={activeClient?.id}
              />
            )}
            {configurationView === 'list' && (
              <UploadDashboard onOpenComparisonDashboard={handleOpenComparisonDashboard} />
            )}
          </motion.div>
        )}
        {view === 'configComparison' && (
          <motion.div
            key="configComparison"
            className="h-full overflow-y-auto"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}>
            {configComparisonView === 'uploads' && (
              <ConfigurationUploadDashboard
                onNavigateToComparisonDashboard={handleNavigateToComparisonDashboard}
              />
            )}
            {configComparisonView === 'comparisonDashboard' && (
              <ConfigurationComparisonDashboard
                onBack={handleBackToConfigUploads}
                onComparisonSelect={handleOpenComparisonResult}
              />
            )}
            {configComparisonView === 'comparisonResult' && (
              <ComparisonResult
                key={selectedComparisonId ?? ''}
                comparisonId={selectedComparisonId}
                comparison={selectedConfigComparison}
                onBack={handleBackToComparisonDashboard}
              />
            )}
          </motion.div>
        )}

        {view === 'settings' && (
          <motion.div
            key="settings"
            className="h-full overflow-y-auto"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}>
            {settingsView === 'user' && <UserSettings embedded />}
            {settingsView === 'tenant' && <TenantSettings embedded />}
            {settingsView === 'environment' && <EnvironmentSettings embedded />}
            {settingsView === 'privacy' && <PrivacyNoticeAdmin />}
            {settingsView === 'disclaimer' && <DisclaimerAdmin />}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }, [view, activeConversationId, setHasActiveMessages, showPolicyDetails, selectedPolicy, configurationView, handleBackToConfigurationList, handleOpenComparisonDashboard, handleOpenAnalysisDetails, handleBackToAnalysisDashboard, selectedComparison, settingsView, configComparisonView, handleNavigateToComparisonDashboard, handleBackToConfigUploads, handleOpenComparisonResult, handleBackToComparisonDashboard, selectedComparisonId, selectedConfigComparison]);

  if (!isProfileReady || !isClientsReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingDots message="Loading your workspace..." />
      </div>
    );
  }

  return (
    <WorkspaceShell
      leftContent={view === 'settings'
        ? <SettingNav active={settingsView} onChange={setSettingsView} onHome={() => handleNavChange('chat')} isAdmin={profile?.isAdmin} />
        : <WorkspaceNav active={view} onChange={handleNavChange} />}
      historyPanel={<ConversationHistoryPanel visible={true} onToggle={() => { /* future toggle */ }} onSelectConversation={handleSelect} />}
      mainContent={mainContent}
      enableHistory={view === 'chat'}
      showNewChatButton={view === 'chat' && hasActiveMessages}
      onNewChat={handleNewChat}
      onBrandClick={() => handleNavChange('chat')}
    />
  );
};

export default WorkspacePage;
